import React from 'react';
import { Link } from 'react-router-dom';
import { Flame, Infinity as Ganzjaehrig } from 'lucide-react';
import { CyclingEvent, EVENT_STATUS_LABEL, EVENT_STATUS_TEXT, POPULAR_LABEL, communityInitials, eventCardModel, friendInterestParts, splitMonYear } from '@radevents/shared';
import BookmarkIcon from './BookmarkIcon';

/**
 * Ein Event als Zeile der Liste neben der Karte (Startseite am Rechner, seit 26.10.2026).
 *
 * Schmaler als die Card, damit neben der Karte fünf Events auf einen Blick stehen statt zwei: links
 * die Datumskachel in der Disziplinfarbe, daneben Kategorie, Titel, Anmeldestand und Ort, darunter
 * die Strecken. Abgeleitet wird nichts hier, sondern in `eventCardModel` wie bei der Card.
 *
 * **Freunde** (`event.friendInterests`, nur angemeldet): kleine Initialen-Kreise und „Anna und Tom
 * dabei“ in der Streckenzeile, rechtsbündig wie auf der Card. Vorbild Meetup und Sweatpals:
 * Gesichter neben dem Termin, nicht als eigener Block.
 *
 * Eine laufende **bezahlte Hervorhebung** zeichnet die Seite weiter als Card (`Landing`), denn die
 * Buchung verspricht genau diese Card mit Rahmen und Kennzeichnung.
 *
 * Wie die Card öffnet der Titel-Link die Detailseite und spannt sich über die ganze Zeile, damit
 * Mausrad- und Strg-Klick einen neuen Tab öffnen (`.event-zeile-link::after` in App.css).
 */

interface Props {
  event: CyclingEvent;
  favorited: boolean;
  onFavorite: (e: React.MouseEvent) => void;
  /** Ursprung der Standortsuche, nur dann steht „ca. X km entfernt“ da. */
  userLat?: number | null;
  userLng?: number | null;
  to: string;
  onNavigate?: (e: React.MouseEvent) => void;
  /** Hervorgehoben, weil die Maus über ihrem Pin auf der Karte steht oder umgekehrt. */
  active?: boolean;
}

const GREY = '#B9B3B3';

const EventZeile: React.FC<Props> = ({ event, favorited, onFavorite, userLat, userLng, to, onNavigate, active }) => {
  const m = eventCardModel(event, { userLat, userLng });
  const color = m.cancelled ? GREY : m.categoryColor;
  const friends = event.friendInterests || [];
  const freunde = friendInterestParts(friends);
  const friendsText = freunde.dabei || freunde.vorgemerkt;
  const place = [m.region, m.distanceKm != null ? `ca. ${m.distanceKm} km entfernt` : null].filter(Boolean).join(' · ');
  // Handy: nur die Stadt, das Bundesland nähme ihr in der einen Zeile den Platz.
  const placeKurz = [event.city || event.areaName || m.region, m.distanceKm != null ? `ca. ${m.distanceKm} km` : null].filter(Boolean).join(' · ');

  return (
    <div className={`event-zeile${active ? ' event-zeile--aktiv' : ''}${m.cancelled ? ' event-zeile--abgesagt' : ''}`}>
      <div className="ez-kachel" style={{ ['--ez-farbe' as string]: color }}>
        {m.date ? (
          <>
            <span className="ez-monat">{splitMonYear(m.date.mon).month}</span>
            <span className={m.date.day.length > 2 ? 'ez-tag ez-tag--spanne' : 'ez-tag'}>{m.date.day}</span>
            <span className="ez-wochentag">{m.date.weekday ? m.date.weekday.slice(0, 2) : ''}</span>
          </>
        ) : (
          <>
            <span className="ez-monat">Immer</span>
            <span className="ez-tag"><Ganzjaehrig size={20} aria-hidden /></span>
            <span className="ez-wochentag" />
          </>
        )}
      </div>

      <div className="ez-inhalt">
        {/* Der Zustand steht vorn in der Kickerzeile: dort schneidet ihn keine Ellipse ab, und die
            Ortszeile behält auf dem Handy Platz für den Ort. */}
        <div className="ez-kicker" style={{ color }}>
          {m.status && <><span className="ez-status" style={{ color: EVENT_STATUS_TEXT[m.status] }}>{EVENT_STATUS_LABEL[m.status]}</span> · </>}
          {m.permanent && m.permanentLabel ? `${m.permanentLabel} · ` : ''}{m.categoryLabel}
        </div>
        <h3 className="ez-titel" style={m.cancelled ? { textDecoration: 'line-through' } : undefined}>
          <Link className="event-zeile-link" to={to} onClick={onNavigate}>{event.title}</Link>
          {m.popular && <span className="ez-beliebt"><Flame size={12} aria-hidden />{POPULAR_LABEL}</span>}
        </h3>
        <div className="ez-ort">
          {m.registration && (
            <span className={m.registration.tone === 'positive' ? 'ez-anmeldung' : 'ez-anmeldung ez-anmeldung--leise'} title={m.registration.long}>
              {m.registration.label}
            </span>
          )}
          {place && <span className="ez-region">
            <span className="hide-mobile">{place}</span><span className="hide-desktop">{placeKurz}</span>
          </span>}
          {/* Handy: Strecken als Spanne in derselben Zeile statt als eigene Chip-Zeile (spart eine Zeile je Event). */}
          {m.kmSpan && <span className="ez-km-kurz">{m.kmSpan}</span>}
        </div>
        {(m.chips.length > 0 || friendsText) && (
          <div className="ez-fuss">
            {m.chips.length > 0 && (
              <div className="ez-chips">
                {m.chips.map((c, i) => <span key={`${c}-${i}`} className="ez-chip">{c}</span>)}
                {m.more && <span className="ez-chip ez-chip--mehr">{m.more}</span>}
              </div>
            )}
            {friendsText && <FreundeAmEvent friends={friends} />}
          </div>
        )}
      </div>

      <button type="button" className="ez-herz" onClick={onFavorite}
        aria-label={favorited ? 'Vormerkung entfernen' : 'Event vormerken'} aria-pressed={favorited}>
        <BookmarkIcon filled={favorited} size={19} />
      </button>
    </div>
  );
};

/**
 * Freunde am Event (30.10.2026): Gesichter, dabei in Beere und gefüllt, vorgemerkt hell mit
 * gestricheltem Rand; dahinter „Lena dabei“ in Beere und „2 vorgemerkt“ grau. Auch in Meine Events.
 */
export function FreundeAmEvent({ friends, className = 'ez-freunde' }: { friends: NonNullable<CyclingEvent['friendInterests']>; className?: string }) {
  const { dabei, vorgemerkt } = friendInterestParts(friends);
  if (!dabei && !vorgemerkt) return null;
  const titel = friends.map(f => `${f.displayName} (${f.status === 'GOING' ? 'dabei' : 'vorgemerkt'})`).join(', ');
  return (
    <span className={className} title={titel}>
      <span className="ez-gesichter" aria-hidden>
        {friends.slice(0, 3).map(f => (
          <span key={f.id} className={f.status === 'GOING' ? 'ez-gesicht ez-gesicht--dabei' : 'ez-gesicht ez-gesicht--vorgemerkt'}>{communityInitials(f.displayName)}</span>
        ))}
      </span>
      <span className="ez-freunde-text">
        {dabei && <span className="ez-freunde-dabei">{dabei}</span>}
        {dabei && vorgemerkt && ', '}
        {vorgemerkt && <span className="ez-freunde-vorgemerkt">{vorgemerkt}</span>}
      </span>
    </span>
  );
}

export default EventZeile;
